import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FaCalendarAlt, FaCheckCircle, FaClock, FaCommentDots, FaEdit, FaEllipsisV, FaExclamationCircle,
  FaFilter, FaSearch, FaSyncAlt, FaTasks, FaTimes, FaTrash, FaUndo, FaUsers,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import TaxPortalLayout from '../components/Common/TaxPortalLayout';
import Loader from '../components/Common/Loader';
import ActionItemsDrawer from './ActionItemsDrawer';
import ActivityTrackingDrawer from '../components/ActionItems/ActivityTrackingDrawer';
import ActionItemStatusDropdown from '../components/ActionItems/ActionItemStatusDropdown';
import { getApiUrl, getUsersEmail } from '../authConfig';
import { getValidToken } from '../utils/msalTokenUtil';
import { calculateAssigneeStats } from '../utils/StatsHelper';
import { useTaxPortal } from '../utils/useTaxPortal';
import TruncatedComment from '../components/Common/TruncatedComment';
import { getAssigneeDisplayName, getAssigneeAvatarGradient } from '../utils/assigneeMapping';
import './DueItemsPage.css';

const getLocalDateString = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatCompletedDate = (updatedAt, dueDate) => {
  if (updatedAt) {
    try {
      const dateObj = new Date(updatedAt.replace(' ', 'T'));
      if (!isNaN(dateObj.getTime())) {
        return dateObj.toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      }
    } catch { }
  }
  if (dueDate) {
    return new Date(`${dueDate}T00:00:00`).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return '—';
};

const getRelativeTimeBadge = (updatedAt, dueDate, today) => {
  const dateStr = updatedAt || dueDate;
  if (!dateStr) return null;
  const itemDay = dateStr.split(' ')[0].split('T')[0];
  if (itemDay === today) {
    return <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle ms-2" style={{ fontSize: '0.68rem' }}>Today</span>;
  }
  const d = new Date(dateStr.replace(' ', 'T'));
  if (!isNaN(d.getTime())) {
    const diffDays = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 1) {
      return <span className="badge rounded-pill bg-light text-secondary border ms-2" style={{ fontSize: '0.68rem' }}>Yesterday</span>;
    }
    if (diffDays > 1 && diffDays <= 7) {
      return <span className="badge rounded-pill bg-light text-secondary border ms-2" style={{ fontSize: '0.68rem' }}>{diffDays}d ago</span>;
    }
    if (diffDays > 7 && diffDays <= 30) {
      return <span className="badge rounded-pill bg-light text-secondary border ms-2" style={{ fontSize: '0.68rem' }}>{diffDays}d ago</span>;
    }
  }
  return null;
};

const ActionMenu = ({ items }) => (
  <div className="dropdown d-inline-block">
    <button
      className="action-menu-trigger dropdown-toggle"
      type="button"
      data-bs-toggle="dropdown"
      aria-expanded="false"
      data-bs-boundary="window"
      aria-label="Action"
      title="Action"
    >
      <FaEllipsisV size={13} />
    </button>
    <ul className="dropdown-menu dropdown-menu-end action-menu-list shadow-sm">
      {items.map((item, i) => (
        item.divider ? (
          <li key={`div-${i}`}><hr className="dropdown-divider my-1" /></li>
        ) : (
          <li key={item.label}>
            <button
              className={`dropdown-item action-menu-item${item.danger ? ' text-danger' : ''}`}
              type="button"
              onClick={item.onClick}
            >
              <span className="action-menu-icon" style={{ color: item.danger ? '#dc2626' : item.color }}>
                <item.icon size={13} />
              </span>
              <span>{item.label}</span>
            </button>
          </li>
        )
      ))}
    </ul>
  </div>
);

const DueItemsPage = () => {
  const portalState = useTaxPortal();
  const { account, msalInstance, isInitialized, isDialerOpen } = portalState;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionItemsTarget, setActionItemsTarget] = useState(null);
  const [activityDrawerItem, setActivityDrawerItem] = useState(null);
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'recently_completed'
  const [completedDatePreset, setCompletedDatePreset] = useState('all'); // 'all' | 'today' | '7days' | '30days' | 'custom'
  const today = getLocalDateString();

  const handleOpenActivityDrawer = (item) => {
    setActivityDrawerItem(item);
  };

  const handleCloseActivityDrawer = () => {
    setActivityDrawerItem(null);
  };

  const handleActivityItemUpdated = (updatedItem) => {
    if (updatedItem?.id) {
      setItems(prev => prev.map(it => it.id === updatedItem.id ? { ...it, ...updatedItem } : it));
      setActivityDrawerItem(prev => prev && prev.id === updatedItem.id ? { ...prev, ...updatedItem } : prev);
    }
    loadDueItems();
  };

  const handleItemStatusChange = (updatedItem, newStatus) => {
    if (updatedItem?.id) {
      setItems(prev => prev.map(it => it.id === updatedItem.id ? { ...it, ...updatedItem, status: newStatus } : it));
      setActivityDrawerItem(prev => prev && prev.id === updatedItem.id ? { ...prev, ...updatedItem, status: newStatus } : prev);
    }
    loadDueItems();
  };

  const handleDeleteItem = async (item) => {
    if (!item?.id) return;
    const confirmMsg = item.customer_name
      ? `Are you sure you want to delete this action item for "${item.customer_name}"?`
      : 'Are you sure you want to delete this action item?';
    if (!window.confirm(confirmMsg)) return;

    try {
      const accessToken = await getValidToken(msalInstance, account);
      const res = await fetch(getApiUrl(`/v1/action-items/${item.id}`), {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.message || 'Unable to delete action item.');
      }
      setItems(prev => prev.filter(it => it.id !== item.id));
      toast.success('Action item deleted successfully.');
    } catch (err) {
      console.error(err);
      toast.error(err.message || 'Could not delete action item.');
    }
  };

  const currentUserEmail = (account?.username || account?.name || '').trim().toLowerCase();
  const isActionAdmin = useMemo(() => {
    return ['admin@orientalbiz.ca', 'info@orientalbiz.ca'].includes(currentUserEmail) ||
      currentUserEmail.startsWith('admin@') ||
      currentUserEmail.startsWith('info@');
  }, [currentUserEmail]);

  const [filters, setFilters] = useState({
    search: '',
    type: 'All',
    assignedTo: 'All',
    datePreset: 'All',
    startDate: '',
    endDate: '',
    status: 'All',
  });

  const loadDueItems = useCallback(async () => {
    if (!account || !isInitialized) return;
    setLoading(true);
    try {
      const accessToken = await getValidToken(msalInstance, account);
      const response = await fetch(getApiUrl('/v1/action-items'), {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      if (!response.ok) throw new Error('Unable to load due items.');
      setItems(await response.json());
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'Could not load due items.');
    } finally {
      setLoading(false);
    }
  }, [account, isInitialized, msalInstance]);

  useEffect(() => {
    loadDueItems();
  }, [loadDueItems]);

  // Role-Based Access Control: Admin and Info roles retain visibility across all system-wide items.
  // All other roles / standard users must see only items specifically assigned to them.
  const roleScopedItems = useMemo(() => {
    if (isActionAdmin) return items;
    return items.filter(item => {
      const assigned = (item.assigned_to || '').trim().toLowerCase();
      if (!assigned) return false;
      return (
        assigned === currentUserEmail ||
        (currentUserEmail && getAssigneeDisplayName(assigned) === getAssigneeDisplayName(currentUserEmail)) ||
        (currentUserEmail && assigned.includes('@') && currentUserEmail.includes('@') && assigned.split('@')[0] === currentUserEmail.split('@')[0])
      );
    });
  }, [items, isActionAdmin, currentUserEmail]);

  const allAssigneeOptions = useMemo(() => {
    if (!isActionAdmin) {
      return currentUserEmail ? [currentUserEmail] : [];
    }
    const envEmails = getUsersEmail();
    const itemEmails = items.map(i => i.assigned_to).filter(Boolean);
    return Array.from(new Set([...envEmails, ...itemEmails])).sort();
  }, [items, isActionAdmin, currentUserEmail]);

  const totalCount = roleScopedItems.length;
  const completedCount = useMemo(() => roleScopedItems.filter(i => i.status === 'Completed').length, [roleScopedItems]);
  const nonCompletedCount = useMemo(() => roleScopedItems.filter(i => i.status !== 'Completed').length, [roleScopedItems]);
  const completionPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  const todayCount = useMemo(() => roleScopedItems.filter(i => i.due_date === today).length, [roleScopedItems, today]);
  const todayPendingCount = useMemo(() => roleScopedItems.filter(i => i.due_date === today && i.status !== 'Completed').length, [roleScopedItems, today]);
  const todayCompletedItems = useMemo(() => roleScopedItems.filter(i => i.due_date === today && i.status === 'Completed'), [roleScopedItems, today]);
  const pastCount = useMemo(() => roleScopedItems.filter(i => i.due_date < today && i.status !== 'Completed').length, [roleScopedItems, today]);
  const futureCount = useMemo(() => roleScopedItems.filter(i => i.due_date > today && i.status !== 'Completed').length, [roleScopedItems, today]);

  const completedTodayCount = useMemo(() => {
    return roleScopedItems.filter(i => {
      if (i.status !== 'Completed') return false;
      const dateStr = (i.updated_at || i.due_date || '').split(' ')[0].split('T')[0];
      return dateStr === today;
    }).length;
  }, [roleScopedItems, today]);

  const completed7DaysCount = useMemo(() => {
    return roleScopedItems.filter(i => {
      if (i.status !== 'Completed') return false;
      const dateStr = (i.updated_at || i.due_date || '').replace(' ', 'T');
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return false;
      const diffDays = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
      return diffDays <= 7;
    }).length;
  }, [roleScopedItems]);

  const assigneeStats = useMemo(() => calculateAssigneeStats(roleScopedItems), [roleScopedItems]);

  const hasActiveFilters = Boolean(
    filters.search.trim() ||
    filters.type !== 'All' ||
    filters.assignedTo !== 'All' ||
    (activeTab === 'active' && (filters.datePreset !== 'All' || filters.startDate || filters.endDate || filters.status !== 'All')) ||
    (activeTab === 'recently_completed' && (completedDatePreset !== 'all' || filters.startDate || filters.endDate))
  );

  const resetFilters = () => {
    setFilters({
      search: '',
      type: 'All',
      assignedTo: 'All',
      datePreset: 'All',
      startDate: '',
      endDate: '',
      status: 'All',
    });
    setCompletedDatePreset('all');
  };

  const handleToggleDateBadge = (preset) => {
    setFilters(prev => ({
      ...prev,
      datePreset: prev.datePreset === preset ? 'All' : preset,
    }));
  };

  const filteredActiveItems = useMemo(() => {
    return roleScopedItems
      .filter((item) => {
        // By default 'All' excludes Completed items from active view, unless user explicitly filters by Completed
        if (filters.status === 'All' && item.status === 'Completed') return false;

        // 1. Search customer / org name or comments or title
        if (filters.search.trim()) {
          const query = filters.search.toLowerCase().trim();
          const matchCustomer = (item.customer_name || '').toLowerCase().includes(query);
          const matchTitle = (item.title || '').toLowerCase().includes(query);
          const matchComments = (item.comments || '').toLowerCase().includes(query) || (item.recent_update || '').toLowerCase().includes(query);
          if (!matchCustomer && !matchTitle && !matchComments) return false;
        }

        // 2. Customer Type (Personal vs Corporate)
        if (filters.type !== 'All' && item.customer_type !== filters.type) {
          return false;
        }

        // 3. Assigned To
        if (filters.assignedTo !== 'All') {
          if (filters.assignedTo === 'Unassigned') {
            if (item.assigned_to && item.assigned_to.trim() !== '') return false;
          } else if (
            item.assigned_to !== filters.assignedTo &&
            getAssigneeDisplayName(item.assigned_to) !== filters.assignedTo &&
            getAssigneeDisplayName(item.assigned_to) !== getAssigneeDisplayName(filters.assignedTo)
          ) {
            return false;
          }
        }

        // 4. Status
        if (filters.status !== 'All' && item.status !== filters.status) {
          return false;
        }

        // 5. Date filter
        if (filters.datePreset === 'past') {
          if (item.due_date >= today) return false;
        } else if (filters.datePreset === 'today') {
          if (item.due_date !== today) return false;
        } else if (filters.datePreset === 'upcoming') {
          if (item.due_date <= today) return false;
        } else if (filters.datePreset === 'custom') {
          if (filters.startDate && item.due_date < filters.startDate) return false;
          if (filters.endDate && item.due_date > filters.endDate) return false;
        }

        return true;
      })
      .sort((a, b) => a.due_date.localeCompare(b.due_date) || a.id - b.id);
  }, [roleScopedItems, filters, today]);

  const filteredCompletedItems = useMemo(() => {
    return roleScopedItems
      .filter((item) => {
        // Only completed action items
        if (item.status !== 'Completed') return false;

        // 1. Search customer / org name, title, or comments
        if (filters.search.trim()) {
          const query = filters.search.toLowerCase().trim();
          const matchCustomer = (item.customer_name || '').toLowerCase().includes(query);
          const matchTitle = (item.title || '').toLowerCase().includes(query);
          const matchComments = (item.comments || '').toLowerCase().includes(query) || (item.recent_update || '').toLowerCase().includes(query);
          if (!matchCustomer && !matchTitle && !matchComments) return false;
        }

        // 2. Customer Type (Personal vs Corporate)
        if (filters.type !== 'All' && item.customer_type !== filters.type) {
          return false;
        }

        // 3. Assigned To
        if (filters.assignedTo !== 'All') {
          if (filters.assignedTo === 'Unassigned') {
            if (item.assigned_to && item.assigned_to.trim() !== '') return false;
          } else if (
            item.assigned_to !== filters.assignedTo &&
            getAssigneeDisplayName(item.assigned_to) !== filters.assignedTo &&
            getAssigneeDisplayName(item.assigned_to) !== getAssigneeDisplayName(filters.assignedTo)
          ) {
            return false;
          }
        }

        // 4. Date filter for completed items (based on completion / updated_at date)
        if (completedDatePreset === 'today') {
          const dateStr = (item.updated_at || item.due_date || '').split(' ')[0].split('T')[0];
          if (dateStr !== today) return false;
        } else if (completedDatePreset === '7days') {
          const dateStr = (item.updated_at || item.due_date || '').replace(' ', 'T');
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return false;
          const diffDays = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
          if (diffDays > 7) return false;
        } else if (completedDatePreset === '30days') {
          const dateStr = (item.updated_at || item.due_date || '').replace(' ', 'T');
          const d = new Date(dateStr);
          if (isNaN(d.getTime())) return false;
          const diffDays = Math.floor((new Date() - d) / (1000 * 60 * 60 * 24));
          if (diffDays > 30) return false;
        } else if (completedDatePreset === 'custom') {
          const dateStr = (item.updated_at || item.due_date || '').split(' ')[0].split('T')[0];
          if (filters.startDate && dateStr < filters.startDate) return false;
          if (filters.endDate && dateStr > filters.endDate) return false;
        }

        return true;
      })
      .sort((a, b) => {
        // Most recently finished first
        const timeA = a.updated_at || a.created_at || a.due_date || '';
        const timeB = b.updated_at || b.created_at || b.due_date || '';
        return timeB.localeCompare(timeA) || b.id - a.id;
      });
  }, [roleScopedItems, filters, completedDatePreset, today]);

  const currentTabItems = activeTab === 'recently_completed' ? filteredCompletedItems : filteredActiveItems;

  return (
    <TaxPortalLayout
      title="Due Items"
      description={
        isActionAdmin
          ? "Action items across personal and corporate customer records, ordered by due date."
          : `Your assigned action items (${currentUserEmail}), ordered by due date.`
      }
      taxEntries={items}
      portalState={portalState}
    >
      {!account ? (
        <div className="alert alert-info">Please sign in with your Oriental Biz account to view due items.</div>
      ) : (
        <>
          {/* Top Metric KPI Cards */}
          <div className="row g-3 mb-4">
            {/* 1. Summary Today */}
            <div className="col-6 col-lg-3">
              <div
                className={`card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card cursor-pointer ${activeTab === 'active' && filters.datePreset === 'today' ? 'border border-2 border-warning' : ''}`}
                onClick={() => {
                  setActiveTab('active');
                  handleToggleDateBadge('today');
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setActiveTab('active');
                    handleToggleDateBadge('today');
                  }
                }}
                title="Click to view Today's Due Items"
              >
                <span
                  className="stat-accent"
                  style={{
                    background: 'linear-gradient(180deg,#f59e0b,#d97706)',
                    boxShadow: '2px 0 10px rgba(245,158,11,0.3)',
                  }}
                />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div
                    className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#fef3c7,#fde68a)' }}
                  >
                    <FaClock size={20} style={{ color: '#d97706' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>
                      Summary Today
                    </small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>
                      {todayPendingCount}
                      {todayCount > todayPendingCount && (
                        <span className="fs-6 text-muted fw-normal"> / {todayCount} total</span>
                      )}
                    </h2>
                  </div>
                </div>
                <div className="mt-3 position-relative z-1 d-flex align-items-center justify-content-between flex-wrap gap-1">
                  <span
                    className="badge rounded-pill fw-medium"
                    style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a', fontSize: '0.68rem' }}
                  >
                    {todayPendingCount > 0 ? `Action needed (${todayPendingCount})` : 'All clear today'}
                    {todayCompletedItems.length > 0 && ` · ${todayCompletedItems.length} completed`}
                  </span>
                  <small className="text-muted" style={{ fontSize: '0.7rem' }}>
                    {nonCompletedCount} open · {futureCount} upcoming
                  </small>
                </div>
              </div>
            </div>

            {/* 2. Completed */}
            <div className="col-6 col-lg-3">
              <div
                className={`card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card cursor-pointer ${activeTab === 'recently_completed' ? 'border border-2 border-success' : ''}`}
                onClick={() => setActiveTab('recently_completed')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setActiveTab('recently_completed');
                  }
                }}
                title="Click to view Recently Completed action items"
              >
                <span
                  className="stat-accent"
                  style={{
                    background: 'linear-gradient(180deg,#22c55e,#15803d)',
                    boxShadow: '2px 0 10px rgba(34,197,94,0.3)',
                  }}
                />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div
                    className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#dcfce7,#bbf7d0)' }}
                  >
                    <FaCheckCircle size={20} style={{ color: '#15803d' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>
                      Completed
                    </small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>
                      {completedCount} <span className="fs-6 text-muted fw-normal">/ {totalCount}</span>
                    </h2>
                  </div>
                </div>
                <div className="position-relative z-1 mt-2">
                  <div className="completion-bar-track">
                    <div className="completion-bar-fill" style={{ width: `${completionPct}%` }} />
                  </div>
                  <small className="text-muted" style={{ fontSize: '0.68rem' }}>
                    {completionPct}% done
                  </small>
                </div>
              </div>
            </div>

            {/* 3. Past Due */}
            <div className="col-6 col-lg-3">
              <div
                className={`card h-100 p-4 border-0 rounded-4 shadow-sm bg-white position-relative overflow-hidden hover-lift stat-card cursor-pointer ${activeTab === 'active' && filters.datePreset === 'past' ? 'border border-2 border-danger' : ''}`}
                onClick={() => {
                  setActiveTab('active');
                  handleToggleDateBadge('past');
                }}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setActiveTab('active');
                    handleToggleDateBadge('past');
                  }
                }}
                title="Click to view Past Due action items"
              >
                <span
                  className="stat-accent"
                  style={{
                    background: 'linear-gradient(180deg,#ef4444,#b91c1c)',
                    boxShadow: '2px 0 10px rgba(239,68,68,0.3)',
                  }}
                />
                <div className="d-flex align-items-center gap-3 position-relative z-1">
                  <div
                    className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 48, height: 48, background: 'linear-gradient(135deg,#fee2e2,#fecaca)' }}
                  >
                    <FaExclamationCircle size={20} style={{ color: '#b91c1c' }} />
                  </div>
                  <div>
                    <small className="text-muted fw-semibold text-uppercase d-block" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>
                      Past Due
                    </small>
                    <h2 className="mb-0 fw-bold" style={{ fontSize: '1.85rem', lineHeight: 1.1 }}>
                      {pastCount}
                    </h2>
                  </div>
                </div>
                <div className="mt-3 position-relative z-1">
                  <span
                    className="badge rounded-pill fw-medium"
                    style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', fontSize: '0.68rem' }}
                  >
                    {pastCount > 0 ? 'Overdue action' : 'No overdue items'}
                  </span>
                </div>
              </div>
            </div>

            {/* 4. Team Queue */}
            <div className="col-6 col-lg-3">
              <div className="card h-100 p-4 border-0 rounded-4 shadow-sm bg-white d-flex flex-column hover-lift stat-card position-relative overflow-hidden">
                <span
                  className="stat-accent"
                  style={{
                    background: 'linear-gradient(180deg,#6366f1,#4338ca)',
                    boxShadow: '2px 0 10px rgba(99,102,241,0.3)',
                  }}
                />
                <div className="d-flex align-items-center gap-2 mb-3 position-relative z-1">
                  <div
                    className="rounded-4 d-flex align-items-center justify-content-center flex-shrink-0"
                    style={{ width: 34, height: 34, background: 'linear-gradient(135deg,#ede9fe,#ddd6fe)' }}
                  >
                    <FaUsers size={15} style={{ color: '#4338ca' }} />
                  </div>
                  <small className="text-muted fw-semibold text-uppercase" style={{ fontSize: '10px', letterSpacing: '0.7px' }}>
                    {isActionAdmin ? 'Team queue · pending / total' : 'My queue · pending / total'}
                  </small>
                </div>
                <div className="flex-grow-1 overflow-auto pe-1 position-relative z-1" style={{ maxHeight: '80px' }}>
                  {assigneeStats.map(([assignee, counts]) => {
                    const isSelected = filters.assignedTo === assignee;
                    return (
                      <div
                        key={assignee}
                        className={`d-flex justify-content-between align-items-center mb-1 rounded px-1 cursor-pointer ${isSelected ? 'bg-light fw-bold' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => {
                          setFilters(prev => ({
                            ...prev,
                            assignedTo: prev.assignedTo === assignee ? 'All' : assignee,
                          }));
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            setFilters(prev => ({
                              ...prev,
                              assignedTo: prev.assignedTo === assignee ? 'All' : assignee,
                            }));
                          }
                        }}
                        title={`Filter by ${assignee}`}
                        style={{ cursor: 'pointer', transition: 'background-color 0.15s ease' }}
                      >
                        <span className="text-truncate fw-medium text-dark" style={{ fontSize: '0.78rem', maxWidth: '140px' }} title={assignee}>
                          {getAssigneeDisplayName(assignee)}
                        </span>
                        <span
                          className="badge rounded-pill"
                          style={{
                            background: isSelected ? '#dbeafe' : '#f1f5f9',
                            color: '#1e40af',
                            border: '1px solid #e2e8f0',
                            fontSize: '0.68rem',
                            padding: '3px 10px',
                          }}
                        >
                          {counts.pending} <span className="text-muted fw-normal">/ {counts.total}</span>
                        </span>
                      </div>
                    );
                  })}
                  {assigneeStats.length === 0 && <span className="text-muted small">No assignments yet.</span>}
                </div>
              </div>
            </div>
          </div>

          <div className="card border-0 rounded-4 shadow-sm overflow-hidden">
            {/* Top Navigation Tabs */}
            <div className="bg-white border-bottom px-4 pt-3">
              <ul className="nav nav-tabs border-bottom-0 gap-3" style={{ fontSize: '0.92rem' }}>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link border-0 pb-3 px-2 fw-semibold d-flex align-items-center gap-2 due-items-nav-tab ${activeTab === 'active' ? 'active text-primary fw-bold' : 'text-muted'}`}
                    style={activeTab === 'active' ? { borderBottom: '3px solid #3b82f6', background: 'transparent' } : { background: 'transparent' }}
                    onClick={() => setActiveTab('active')}
                  >
                    <FaTasks />
                    <span>Active Due Items</span>
                    <span className="badge rounded-pill bg-primary-subtle text-primary border border-primary-subtle" style={{ fontSize: '0.72rem' }}>
                      {nonCompletedCount}
                    </span>
                    {pastCount > 0 && (
                      <span className="badge rounded-pill bg-danger text-white ms-1 shadow-sm" style={{ fontSize: '0.68rem', padding: '2px 7px' }}>
                        {pastCount} overdue
                      </span>
                    )}
                  </button>
                </li>
                <li className="nav-item">
                  <button
                    type="button"
                    className={`nav-link border-0 pb-3 px-2 fw-semibold d-flex align-items-center gap-2 due-items-nav-tab ${activeTab === 'recently_completed' ? 'active text-success fw-bold' : 'text-muted'}`}
                    style={activeTab === 'recently_completed' ? { borderBottom: '3px solid #16a34a', background: 'transparent' } : { background: 'transparent' }}
                    onClick={() => setActiveTab('recently_completed')}
                  >
                    <FaCheckCircle className="text-success" />
                    <span>Recently Completed</span>
                    <span className="badge rounded-pill bg-success-subtle text-success border border-success-subtle" style={{ fontSize: '0.72rem' }}>
                      {completedCount}
                    </span>
                  </button>
                </li>
              </ul>
            </div>

            {/* Header with summary badges */}
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 px-4 py-3 border-bottom bg-white">
              <div>
                <h2 className="h5 fw-bold mb-1">
                  {activeTab === 'recently_completed' ? 'Recently Completed' : 'Due Items'}
                </h2>
                <p className="text-muted small mb-0">
                  {activeTab === 'recently_completed' ? (
                    hasActiveFilters ? (
                      <>Showing <strong>{currentTabItems.length}</strong> of <strong>{completedCount}</strong> finished action items</>
                    ) : (
                      <>{completedCount} completed {completedCount === 1 ? 'item' : 'items'} (ordered by latest finish date)</>
                    )
                  ) : (
                    hasActiveFilters ? (
                      <>Showing <strong>{currentTabItems.length}</strong> {filters.status === 'Completed' ? 'completed action items' : <>of <strong>{nonCompletedCount}</strong> active action items</>}</>
                    ) : (
                      <>{nonCompletedCount} active action {nonCompletedCount === 1 ? 'item' : 'items'}</>
                    )
                  )}
                </p>
              </div>
              <div className="d-flex flex-wrap align-items-center gap-2 small">
                {activeTab === 'recently_completed' ? (
                  <>
                    <span
                      role="button"
                      tabIndex={0}
                      className={`badge due-filter-badge ${completedDatePreset === 'all' ? 'active-filter' : ''}`}
                      style={{ background: '#f1f5f9', color: '#1e293b' }}
                      onClick={() => setCompletedDatePreset('all')}
                      onKeyDown={(e) => { if (e.key === 'Enter') setCompletedDatePreset('all'); }}
                      title="Show all completed items"
                    >
                      All ({completedCount})
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className={`badge due-filter-badge ${completedDatePreset === 'today' ? 'active-filter' : ''}`}
                      style={{ background: '#dcfce7', color: '#15803d' }}
                      onClick={() => setCompletedDatePreset(prev => prev === 'today' ? 'all' : 'today')}
                      onKeyDown={(e) => { if (e.key === 'Enter') setCompletedDatePreset(prev => prev === 'today' ? 'all' : 'today'); }}
                      title="Show items completed today"
                    >
                      Today ({completedTodayCount})
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className={`badge due-filter-badge ${completedDatePreset === '7days' ? 'active-filter' : ''}`}
                      style={{ background: '#e0f2fe', color: '#0369a1' }}
                      onClick={() => setCompletedDatePreset(prev => prev === '7days' ? 'all' : '7days')}
                      onKeyDown={(e) => { if (e.key === 'Enter') setCompletedDatePreset(prev => prev === '7days' ? 'all' : '7days'); }}
                      title="Show items completed in the last 7 days"
                    >
                      Last 7 Days ({completed7DaysCount})
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 ms-1"
                      onClick={loadDueItems}
                      title="Reload action items from server"
                    >
                      <FaSyncAlt className={loading ? 'fa-spin' : ''} size={11} /> Refresh
                    </button>
                  </>
                ) : (
                  <>
                    <span
                      role="button"
                      tabIndex={0}
                      className={`badge due-filter-badge ${filters.datePreset === 'past' ? 'active-filter' : ''}`}
                      style={{ background: '#fee2e2', color: '#991b1b' }}
                      onClick={() => handleToggleDateBadge('past')}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleToggleDateBadge('past'); }}
                      title="Click to filter Past Due items"
                    >
                      Past due ({pastCount})
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className={`badge due-filter-badge ${filters.datePreset === 'today' ? 'active-filter' : ''}`}
                      style={{ background: '#fef3c7', color: '#92400e' }}
                      onClick={() => handleToggleDateBadge('today')}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleToggleDateBadge('today'); }}
                      title="Click to filter items Due Today"
                    >
                      Due today ({todayPendingCount})
                    </span>
                    <span
                      role="button"
                      tabIndex={0}
                      className={`badge due-filter-badge ${filters.datePreset === 'upcoming' ? 'active-filter' : ''}`}
                      style={{ background: '#dcfce7', color: '#166534' }}
                      onClick={() => handleToggleDateBadge('upcoming')}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleToggleDateBadge('upcoming'); }}
                      title="Click to filter Future Due items"
                    >
                      Future due ({futureCount})
                    </span>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary d-flex align-items-center gap-1 ms-1"
                      onClick={loadDueItems}
                      title="Reload action items from server"
                    >
                      <FaSyncAlt className={loading ? 'fa-spin' : ''} size={11} /> Refresh
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Filter Bar */}
            <div className="due-items-filter-bar">
              <div className="row g-2 align-items-center">
                {/* Search Org / Customer */}
                <div className="col-12 col-md-4 col-lg-3">
                  <div className="input-group input-group-sm">
                    <span className="input-group-text bg-white text-muted">
                      <FaSearch />
                    </span>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search customer / org / notes..."
                      value={filters.search}
                      onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                      aria-label="Filter by organization or customer"
                    />
                    {filters.search && (
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => setFilters(prev => ({ ...prev, search: '' }))}
                        aria-label="Clear search text"
                      >
                        <FaTimes />
                      </button>
                    )}
                  </div>
                </div>

                {/* Filter by Type */}
                <div className="col-6 col-md-3 col-lg-2">
                  <select
                    className="form-select form-select-sm"
                    value={filters.type}
                    onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                    aria-label="Filter by type"
                  >
                    <option value="All">All Types (Personal & Corp)</option>
                    <option value="Personal">Personal</option>
                    <option value="Corporate">Corporate</option>
                  </select>
                </div>

                {/* Filter by Assigned To */}
                <div className="col-6 col-md-3 col-lg-3">
                  {isActionAdmin ? (
                    <select
                      className="form-select form-select-sm"
                      value={filters.assignedTo}
                      onChange={(e) => setFilters(prev => ({ ...prev, assignedTo: e.target.value }))}
                      aria-label="Filter by assignee"
                    >
                      <option value="All">All Assignees</option>
                      <option value="Unassigned">Unassigned</option>
                      {allAssigneeOptions.map(email => (
                        <option key={email} value={email}>
                          {getAssigneeDisplayName(email)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <div
                      className="badge bg-light text-secondary border py-2 px-2 w-100 text-truncate text-center fw-medium d-flex align-items-center justify-content-center gap-1"
                      title={`Showing items assigned to ${currentUserEmail}`}
                      style={{ fontSize: '0.8rem', height: '31px' }}
                    >
                      <span>Assigned to: {getAssigneeDisplayName(currentUserEmail)}</span>
                    </div>
                  )}
                </div>

                {/* Filter by Date Preset */}
                <div className="col-6 col-md-2 col-lg-2">
                  {activeTab === 'recently_completed' ? (
                    <select
                      className="form-select form-select-sm"
                      value={completedDatePreset}
                      onChange={(e) => setCompletedDatePreset(e.target.value)}
                      aria-label="Filter completed date"
                    >
                      <option value="all">All Finished</option>
                      <option value="today">Finished Today</option>
                      <option value="7days">Last 7 Days</option>
                      <option value="30days">Last 30 Days</option>
                      <option value="custom">Custom Date Range...</option>
                    </select>
                  ) : (
                    <select
                      className="form-select form-select-sm"
                      value={filters.datePreset}
                      onChange={(e) => setFilters(prev => ({ ...prev, datePreset: e.target.value }))}
                      aria-label="Filter by date"
                    >
                      <option value="All">All Dates</option>
                      <option value="past">Past Due</option>
                      <option value="today">Due Today</option>
                      <option value="upcoming">Future Due</option>
                      <option value="custom">Custom Date Range...</option>
                    </select>
                  )}
                </div>

                {/* Filter by Status */}
                <div className="col-6 col-md-2 col-lg-2">
                  {activeTab === 'recently_completed' ? (
                    <div className="badge bg-success-subtle text-success border border-success-subtle py-2 px-2 w-100 text-center fw-medium">
                      <FaCheckCircle className="me-1" /> Status: Completed
                    </div>
                  ) : (
                    <select
                      className="form-select form-select-sm"
                      value={filters.status}
                      onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                      aria-label="Filter by status"
                    >
                      <option value="All">All Statuses</option>
                      <option value="Open">Open</option>
                      <option value="In Progress">In Progress</option>
                      <option value="Completed">Completed</option>
                    </select>
                  )}
                </div>
              </div>

              {/* Custom Date Range Row (visible when 'custom' is selected) */}
              {filters.datePreset === 'custom' && (
                <div className="row g-2 align-items-center mt-2 pt-2 border-top">
                  <div className="col-auto small text-muted fw-semibold d-flex align-items-center">
                    <FaCalendarAlt className="me-1 text-primary" /> Date Range:
                  </div>
                  <div className="col-auto">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white text-muted">From</span>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.startDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, startDate: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="col-auto">
                    <div className="input-group input-group-sm">
                      <span className="input-group-text bg-white text-muted">To</span>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.endDate}
                        onChange={(e) => setFilters(prev => ({ ...prev, endDate: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Active Filter Tags & Reset */}
              {hasActiveFilters && (
                <div className="d-flex flex-wrap align-items-center gap-2 mt-2 pt-2 border-top small">
                  <span className="text-muted d-flex align-items-center gap-1">
                    <FaFilter size={11} /> Active Filters:
                  </span>
                  {filters.search && (
                    <span className="badge bg-white text-dark border">
                      Search: "{filters.search}"
                    </span>
                  )}
                  {filters.type !== 'All' && (
                    <span className="badge bg-white text-dark border">
                      Type: {filters.type}
                    </span>
                  )}
                  {filters.assignedTo !== 'All' && (
                    <span className="badge bg-white text-dark border">
                      Assigned: {filters.assignedTo}
                    </span>
                  )}
                  {filters.datePreset !== 'All' && (
                    <span className="badge bg-white text-dark border">
                      Date: {filters.datePreset === 'custom'
                        ? `${filters.startDate || 'Any'} to ${filters.endDate || 'Any'}`
                        : filters.datePreset === 'past' ? 'Past Due' : filters.datePreset === 'today' ? 'Due Today' : 'Future Due'}
                    </span>
                  )}
                  {activeTab === 'active' && filters.status !== 'All' && (
                    <span className="badge bg-white text-dark border">
                      Status: {filters.status}
                    </span>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-link text-danger text-decoration-none p-0 ms-auto d-flex align-items-center gap-1"
                    onClick={resetFilters}
                  >
                    <FaUndo size={11} /> Clear all filters
                  </button>
                </div>
              )}
            </div>

            {/* Informational banner when filtered by Today in Active Tab and there are completed items today */}
            {activeTab === 'active' && filters.datePreset === 'today' && filters.status !== 'Completed' && todayCompletedItems.length > 0 && (
              <div
                className="mx-4 mt-3 mb-1 p-3 rounded-3 border d-flex align-items-center justify-content-between flex-wrap gap-2"
                style={{ background: '#f0fdf4', borderColor: '#bbf7d0' }}
              >
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="rounded-circle d-flex align-items-center justify-content-center"
                    style={{ width: 28, height: 28, background: '#dcfce7', color: '#166534', flexShrink: 0 }}
                  >
                    <FaCheckCircle size={14} />
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#166534' }}>
                    <strong>{todayCompletedItems.length}</strong> {todayCompletedItems.length === 1 ? 'item' : 'items'} due today was already marked completed:{' '}
                    <strong>{todayCompletedItems.map(i => i.customer_name).join(', ')}</strong>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-success fw-semibold d-flex align-items-center gap-1 shadow-sm"
                  style={{ fontSize: '0.78rem' }}
                  onClick={() => {
                    setActiveTab('recently_completed');
                    setCompletedDatePreset('today');
                  }}
                >
                  <FaCheckCircle size={11} /> View in Recently Completed &rarr;
                </button>
              </div>
            )}

            {/* Table or Empty State */}
            {loading ? (
              <div className="p-4 text-muted">Loading action items...</div>
            ) : items.length === 0 ? (
              <div className="due-items-empty">
                <div className="due-items-empty-icon"><FaCalendarAlt /></div>
                <div className="fw-semibold">No action items found</div>
                <div className="small text-muted mt-1">There are no action items registered across customer accounts yet.</div>
              </div>
            ) : currentTabItems.length === 0 ? (
              <div className="due-items-empty">
                {activeTab === 'recently_completed' ? (
                  <>
                    <div className="due-items-empty-icon text-success"><FaCheckCircle /></div>
                    <div className="fw-semibold">No recently completed items</div>
                    <div className="small text-muted mt-1 mb-3">
                      {hasActiveFilters
                        ? 'No completed action items match your current filter criteria.'
                        : 'Action items marked as Completed will appear here with completion timestamps.'}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="due-items-empty-icon"><FaSearch /></div>
                    <div className="fw-semibold">No matching action items</div>
                    <div className="small text-muted mt-1 mb-3">No active action items match your current filter selections.</div>
                  </>
                )}
                {hasActiveFilters && (
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={resetFilters}
                  >
                    <FaUndo className="me-1" /> Reset all filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {/* Desktop View (>= md) */}
                <div className="d-none d-md-block table-responsive">
                  <table className="table align-middle mb-0 due-items-table">
                    <thead className="table-light">
                      <tr>
                        <th className="ps-4" style={{ minWidth: 160 }}>{activeTab === 'recently_completed' ? 'Finished / Due Date' : 'Due Date'}</th>
                        <th style={{ minWidth: 200, maxWidth: 240 }}>Customer / Corporate Name</th>
                        <th style={{ minWidth: 190, maxWidth: 220 }}>Comments</th>
                        <th style={{ minWidth: 190, maxWidth: 220 }}>Recent Update</th>
                        <th style={{ minWidth: 140, maxWidth: 160 }}>{activeTab === 'recently_completed' ? 'Completed / Assigned' : 'Assigned To'}</th>
                        <th style={{ minWidth: 120 }}>Status</th>
                        <th style={{ minWidth: 90 }}>Type</th>
                        <th className="pe-4 text-center" style={{ width: 70, minWidth: 70 }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {currentTabItems.map(item => {
                        const itemDate = new Date(`${item.due_date}T00:00:00`);

                        // Recently Completed Tab Row
                        if (activeTab === 'recently_completed') {
                          return (
                            <tr key={item.id} className="hover-table-row">
                              <td className="ps-4 text-nowrap">
                                <div className="d-flex align-items-center gap-2">
                                  <FaCheckCircle className="text-success flex-shrink-0" size={14} />
                                  <div>
                                    <div className="d-flex align-items-center">
                                      <span className="fw-semibold text-dark">
                                        {formatCompletedDate(item.updated_at, item.due_date)}
                                      </span>
                                      {getRelativeTimeBadge(item.updated_at, item.due_date, today)}
                                    </div>
                                    <small className="text-muted" style={{ fontSize: '0.72rem' }}>
                                      Due: {itemDate.toLocaleDateString()}
                                    </small>
                                  </div>
                                </div>
                              </td>
                              <td className="fw-semibold text-dark text-nowrap text-truncate" style={{ maxWidth: 220 }} title={item.customer_name}>
                                {item.customer_name || 'Deleted customer'}
                              </td>
                              <td className="due-comment-cell text-secondary">
                                {item.title && (
                                  <div className="fw-semibold text-dark text-truncate mb-1" style={{ maxWidth: 220 }} title={item.title}>
                                    {item.title}
                                  </div>
                                )}
                                <TruncatedComment text={item.comments} maxLength={item.title ? 20 : 28} />
                              </td>
                              <td className="due-comment-cell text-secondary">
                                {item.recent_update ? (
                                  <div className="recent-update-bubble text-truncate">
                                    <FaCommentDots size={12} className="flex-shrink-0" />
                                    <TruncatedComment text={item.recent_update} maxLength={24} />
                                  </div>
                                ) : (
                                  <span className="recent-update-empty">No updates yet</span>
                                )}
                              </td>
                              <td className="text-nowrap" style={{ minWidth: 140, maxWidth: 160 }}>
                                {item.assigned_to ? (
                                  <div className="d-flex align-items-center gap-2">
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0 shadow-sm"
                                      style={{
                                        width: 26,
                                        height: 26,
                                        fontSize: '0.68rem',
                                        background: getAssigneeAvatarGradient(item.assigned_to),
                                      }}
                                    >
                                      {(getAssigneeDisplayName(item.assigned_to)[0] || 'U').toUpperCase()}
                                    </div>
                                    <span
                                      className="text-dark fw-medium text-truncate text-capitalize"
                                      style={{ maxWidth: 110 }}
                                      title={`${getAssigneeDisplayName(item.assigned_to)} (${item.assigned_to})`}
                                    >
                                      {getAssigneeDisplayName(item.assigned_to)}
                                    </span>
                                  </div>
                                ) : (
                                  <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '0.72rem', border: '1px solid #e2e8f0' }}>
                                    Unassigned
                                  </span>
                                )}
                              </td>
                              <td>
                                <ActionItemStatusDropdown
                                  item={item}
                                  onStatusChange={handleItemStatusChange}
                                  account={account}
                                  msalInstance={msalInstance}
                                />
                              </td>
                              <td>
                                <span className="badge rounded-pill" style={{ background: '#fff', border: '1px solid #e2e8f0', color: item.customer_type === 'Personal' ? '#1d4ed8' : '#6d28d9' }}>
                                  {item.customer_type}
                                </span>
                              </td>
                              <td className="pe-4 text-center text-nowrap">
                                <ActionMenu
                                  items={[
                                    {
                                      label: 'Edit',
                                      icon: FaEdit,
                                      color: '#2563eb',
                                      onClick: () => setActionItemsTarget(item),
                                    },
                                    {
                                      label: 'Add Comments',
                                      icon: FaCommentDots,
                                      color: '#059669',
                                      onClick: () => handleOpenActivityDrawer(item),
                                    },
                                    { divider: true },
                                    {
                                      label: 'Delete',
                                      icon: FaTrash,
                                      color: '#dc2626',
                                      danger: true,
                                      onClick: () => handleDeleteItem(item),
                                    },
                                  ]}
                                />
                              </td>
                            </tr>
                          );
                        }

                        // Active Tab Row
                        const isPastDue = item.due_date < today;
                        const isDueToday = item.due_date === today;
                        const rowStyle = isPastDue ? { backgroundColor: '#fef2f2' } : { backgroundColor: '#ffffff' };

                        return (
                          <tr key={item.id} className="hover-table-row" style={rowStyle}>
                            <td
                              className="ps-4 text-nowrap"
                              style={isDueToday ? { backgroundColor: '#fef9c3', borderLeft: '3px solid #f59e0b' } : isPastDue ? { borderLeft: '3px solid #ef4444' } : undefined}
                            >
                              <span className="fw-semibold text-dark">{itemDate.toLocaleDateString()}</span>
                              {isPastDue && (
                                <span className="badge rounded-pill ms-2" style={{ background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5', fontSize: '0.68rem' }}>
                                  Past due
                                </span>
                              )}
                              {isDueToday && (
                                <span className="badge rounded-pill ms-2" style={{ background: '#fef08a', color: '#854d0e', border: '1px solid #fde047', fontSize: '0.68rem' }}>
                                  Due today
                                </span>
                              )}
                              {!isPastDue && !isDueToday && (
                                <span className="badge rounded-pill ms-2" style={{ background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', fontSize: '0.68rem' }}>
                                  Upcoming
                                </span>
                              )}
                            </td>
                            <td className="fw-semibold text-dark text-nowrap text-truncate" style={{ maxWidth: 220 }} title={item.customer_name}>
                              {item.customer_name || 'Deleted customer'}
                            </td>
                            <td className="due-comment-cell text-secondary">
                              {item.title && (
                                <div className="fw-semibold text-dark text-truncate mb-1" style={{ maxWidth: 220 }} title={item.title}>
                                  {item.title}
                                </div>
                              )}
                              <TruncatedComment text={item.comments} maxLength={item.title ? 20 : 28} />
                            </td>
                            <td className="due-comment-cell text-secondary">
                              {item.recent_update ? (
                                <div className="recent-update-bubble text-truncate">
                                  <FaCommentDots size={12} className="flex-shrink-0" />
                                  <TruncatedComment text={item.recent_update} maxLength={24} />
                                </div>
                              ) : (
                                <span className="recent-update-empty">No updates yet</span>
                              )}
                            </td>
                            <td className="text-nowrap" style={{ minWidth: 140, maxWidth: 160 }}>
                              {item.assigned_to ? (
                                <div className="d-flex align-items-center gap-2">
                                  <div
                                    className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0 shadow-sm"
                                    style={{
                                      width: 26,
                                      height: 26,
                                      fontSize: '0.68rem',
                                      background: getAssigneeAvatarGradient(item.assigned_to),
                                    }}
                                  >
                                    {(getAssigneeDisplayName(item.assigned_to)[0] || 'U').toUpperCase()}
                                  </div>
                                  <span
                                    className="text-dark fw-medium text-truncate text-capitalize"
                                    style={{ maxWidth: 110 }}
                                    title={`${getAssigneeDisplayName(item.assigned_to)} (${item.assigned_to})`}
                                  >
                                    {getAssigneeDisplayName(item.assigned_to)}
                                  </span>
                                </div>
                              ) : (
                                <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#64748b', fontSize: '0.72rem', border: '1px solid #e2e8f0' }}>
                                  Unassigned
                                </span>
                              )}
                            </td>
                            <td>
                              <ActionItemStatusDropdown
                                item={item}
                                onStatusChange={handleItemStatusChange}
                                account={account}
                                msalInstance={msalInstance}
                              />
                            </td>
                            <td>
                              <span className="badge rounded-pill" style={{ background: '#fff', border: '1px solid #e2e8f0', color: item.customer_type === 'Personal' ? '#1d4ed8' : '#6d28d9' }}>
                                {item.customer_type}
                              </span>
                            </td>
                            <td className="pe-4 text-center text-nowrap">
                              <ActionMenu
                                items={[
                                  {
                                    label: 'Edit',
                                    icon: FaEdit,
                                    color: '#2563eb',
                                    onClick: () => setActionItemsTarget(item),
                                  },
                                  {
                                    label: 'Add Comments',
                                    icon: FaCommentDots,
                                    color: '#059669',
                                    onClick: () => handleOpenActivityDrawer(item),
                                  },
                                ]}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Mobile View (< md) */}
                <div className="d-block d-md-none p-2 p-sm-3">
                  <div className="d-flex flex-column gap-3">
                    {currentTabItems.map(item => {
                      const itemDate = new Date(`${item.due_date}T00:00:00`);
                      const isPastDue = item.due_date < today;
                      const isDueToday = item.due_date === today;
                      const accentColor = item.status === 'Completed'
                        ? '#16a34a'
                        : isPastDue
                          ? '#ef4444'
                          : isDueToday
                            ? '#f59e0b'
                            : '#3b82f6';

                      return (
                        <div key={item.id} className="card due-mobile-card border-0 rounded-4 shadow-sm bg-white overflow-hidden position-relative">
                          {/* Top Status Accent Bar */}
                          <div style={{ height: 4, background: accentColor }} />

                          <div className="card-body p-3">
                            {/* Header: Customer Name, Type badge, and 3-dot Action Menu */}
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                              <div className="flex-grow-1" style={{ minWidth: 0 }}>
                                <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                                  <span
                                    className="badge rounded-pill"
                                    style={{
                                      background: '#fff',
                                      border: '1px solid #e2e8f0',
                                      color: item.customer_type === 'Personal' ? '#1d4ed8' : '#6d28d9',
                                      fontSize: '0.68rem',
                                    }}
                                  >
                                    {item.customer_type}
                                  </span>
                                  {activeTab === 'recently_completed' ? (
                                    <span className="badge rounded-pill" style={{ background: '#dcfce7', color: '#166534', fontSize: '0.68rem' }}>
                                      Finished
                                    </span>
                                  ) : isPastDue ? (
                                    <span className="badge rounded-pill" style={{ background: '#fee2e2', color: '#991b1b', fontSize: '0.68rem' }}>
                                      Past due
                                    </span>
                                  ) : isDueToday ? (
                                    <span className="badge rounded-pill" style={{ background: '#fef08a', color: '#854d0e', fontSize: '0.68rem' }}>
                                      Due today
                                    </span>
                                  ) : (
                                    <span className="badge rounded-pill" style={{ background: '#f1f5f9', color: '#475569', fontSize: '0.68rem' }}>
                                      Upcoming
                                    </span>
                                  )}
                                </div>
                                <h6 className="fw-bold text-dark mb-0 text-truncate" title={item.customer_name}>
                                  {item.customer_name || 'Deleted customer'}
                                </h6>
                                {item.title && (
                                  <div className="small fw-semibold text-primary mt-1 text-truncate" title={item.title}>
                                    {item.title}
                                  </div>
                                )}
                              </div>

                              {/* 3-Dot Action Menu */}
                              <ActionMenu
                                items={[
                                  {
                                    label: 'Edit',
                                    icon: FaEdit,
                                    color: '#2563eb',
                                    onClick: () => setActionItemsTarget(item),
                                  },
                                  {
                                    label: 'Add Comments',
                                    icon: FaCommentDots,
                                    color: '#059669',
                                    onClick: () => handleOpenActivityDrawer(item),
                                  },
                                  ...(activeTab === 'recently_completed'
                                    ? [
                                      { divider: true },
                                      {
                                        label: 'Delete',
                                        icon: FaTrash,
                                        color: '#dc2626',
                                        danger: true,
                                        onClick: () => handleDeleteItem(item),
                                      },
                                    ]
                                    : []),
                                ]}
                              />
                            </div>

                            {/* Date and Assignee Row */}
                            <div className="d-flex align-items-center justify-content-between pt-2 border-top small text-muted mb-2">
                              <div className="d-flex align-items-center gap-1">
                                <FaCalendarAlt size={12} className={isPastDue ? 'text-danger' : 'text-primary'} />
                                <span className="fw-semibold text-dark">
                                  {activeTab === 'recently_completed'
                                    ? formatCompletedDate(item.updated_at, item.due_date)
                                    : itemDate.toLocaleDateString()}
                                </span>
                              </div>

                              {/* Assigned To with Avatar and Mapped Name */}
                              <div className="d-flex align-items-center gap-2">
                                {item.assigned_to ? (
                                  <>
                                    <div
                                      className="rounded-circle d-flex align-items-center justify-content-center text-white fw-bold flex-shrink-0"
                                      style={{
                                        width: 22,
                                        height: 22,
                                        fontSize: '0.62rem',
                                        background: getAssigneeAvatarGradient(item.assigned_to),
                                      }}
                                    >
                                      {(getAssigneeDisplayName(item.assigned_to)[0] || 'U').toUpperCase()}
                                    </div>
                                    <span className="text-dark fw-medium small text-capitalize">
                                      {getAssigneeDisplayName(item.assigned_to)}
                                    </span>
                                  </>
                                ) : (
                                  <span className="badge rounded-pill bg-light text-muted border" style={{ fontSize: '0.68rem' }}>
                                    Unassigned
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Status and Action Buttons */}
                            <div className="d-flex align-items-center justify-content-between mb-3">
                              <div>
                                <small className="text-muted d-block" style={{ fontSize: '0.68rem', textTransform: 'uppercase' }}>
                                  Status
                                </small>
                                <ActionItemStatusDropdown
                                  item={item}
                                  onStatusChange={handleItemStatusChange}
                                  account={account}
                                  msalInstance={msalInstance}
                                />
                              </div>

                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-1 rounded-pill px-3 shadow-sm"
                                style={{ fontSize: '0.78rem' }}
                                onClick={() => handleOpenActivityDrawer(item)}
                              >
                                <FaCommentDots size={12} /> Add Comments
                              </button>
                            </div>

                            {/* Original Comments */}
                            {item.comments && (
                              <div className="p-2 rounded-3 bg-light border mb-2 small text-secondary">
                                <span className="fw-semibold text-dark d-block mb-1" style={{ fontSize: '0.72rem' }}>
                                  Task Note:
                                </span>
                                <span className="text-break">{item.comments}</span>
                              </div>
                            )}

                            {/* Recent Update (if any) */}
                            <div className="pt-2 border-top">
                              <span className="fw-semibold text-dark d-block mb-1" style={{ fontSize: '0.72rem' }}>
                                Recent Update:
                              </span>
                              {item.recent_update ? (
                                <div className="recent-update-bubble w-100 text-break">
                                  <FaCommentDots size={12} className="flex-shrink-0" />
                                  <span>{item.recent_update}</span>
                                </div>
                              ) : (
                                <span className="recent-update-empty">No updates yet</span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <Loader variant="overlay" show={loading} text="Loading due items..." />

      <ActionItemsDrawer
        isOpen={Boolean(actionItemsTarget)}
        onClose={() => {
          setActionItemsTarget(null);
          loadDueItems();
        }}
        customer={actionItemsTarget ? {
          id: actionItemsTarget.customer_id,
          _id: actionItemsTarget.customer_id,
          name: actionItemsTarget.customer_name,
          businessName: actionItemsTarget.customer_name,
        } : null}
        customerType={actionItemsTarget?.customer_type || 'Personal'}
        initialEditItem={actionItemsTarget}
        account={account}
        msalInstance={msalInstance}
        isDialerOpen={isDialerOpen}
      />

      <ActivityTrackingDrawer
        isOpen={Boolean(activityDrawerItem)}
        onClose={handleCloseActivityDrawer}
        item={activityDrawerItem}
        account={account}
        msalInstance={msalInstance}
        onItemUpdated={handleActivityItemUpdated}
      />
    </TaxPortalLayout>
  );
};

export default DueItemsPage;
